/**
 * Built-in default hand poses.
 *
 * Each pose stores per-bone local-frame delta quaternions (relative to rest).
 * Deltas are defined for the LEFT hand; right-hand deltas are derived at
 * apply-time by negating the Y and Z quaternion components (sagittal mirror).
 *
 * Bone keys use the suffix form "thumb1", "index2", etc.  The full bone name
 * is constructed as `finger_${key}${side}${joint}` at apply-time.
 */

import * as THREE from "three/webgpu";
import { getRestPose } from "../mannequin/boneRegistry.ts";
import { FINGER_SPECS } from "../mannequin/fingerData.ts";
import {
  mirrorQuaternion,
  type Quat4,
  qmul,
} from "../mannequin/quaternionMath.ts";
import { useStore } from "./store.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Q4 = Quat4;

export interface HandPose {
  id: string;
  name: string;
  /** Per-bone delta quaternions for the LEFT hand.  Key = "thumb1" … "pinky3". */
  deltas: Record<string, Q4>;
}

// ---------------------------------------------------------------------------
// Quaternion helpers
// ---------------------------------------------------------------------------

/** Axis-angle → quaternion tuple.  Axis is normalized internally. */
function aa(x: number, y: number, z: number, deg: number): Q4 {
  const q = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(x, y, z).normalize(),
    deg * (Math.PI / 180),
  );
  return [q.x, q.y, q.z, q.w];
}

const IDENTITY: Q4 = [0, 0, 0, 1];

// ---------------------------------------------------------------------------
// Axis shorthands (must match HandPosePanel curl axes)
// ---------------------------------------------------------------------------

/** Flexion around −X (all fingers except thumb CMC). */
const flexX = (deg: number): Q4 => aa(-1, 0, 0, deg);

/** Thumb CMC compound axis — left hand.  (0.885, 0, 0.465) normalized. */
const thumbCmc = (deg: number): Q4 => aa(0.885, 0, 0.465, deg);

/** Splay (abduction) around local Z. */
const splayZ = (deg: number): Q4 => aa(0, 0, 1, deg);

// ---------------------------------------------------------------------------
// Default hand poses
// ---------------------------------------------------------------------------

/**
 * Build full-curl (fist) deltas for one finger from anatomical specs.
 * Uses dipFlex directly — the coupling ratio is for intermediate positions.
 */
function fullCurlDeltas(finger: string): Record<string, Q4> {
  const s = FINGER_SPECS[finger];
  return {
    [`${finger}1`]: finger === "thumb" ? thumbCmc(s.mcpFlex) : flexX(s.mcpFlex),
    [`${finger}2`]: flexX(s.pipFlex),
    [`${finger}3`]: flexX(s.dipFlex),
  };
}

const FIST: HandPose = {
  id: "hand:fist",
  name: "Fist",
  deltas: {
    ...fullCurlDeltas("thumb"),
    ...fullCurlDeltas("index"),
    ...fullCurlDeltas("middle"),
    ...fullCurlDeltas("ring"),
    ...fullCurlDeltas("pinky"),
  },
};

const PEACE: HandPose = {
  id: "hand:peace",
  name: "Peace",
  deltas: {
    ...fullCurlDeltas("thumb"),
    index1: splayZ(-8), // V-splay
    index2: IDENTITY,
    index3: IDENTITY,
    middle1: splayZ(8), // V-splay
    middle2: IDENTITY,
    middle3: IDENTITY,
    ...fullCurlDeltas("ring"),
    ...fullCurlDeltas("pinky"),
  },
};

const HOLDING: HandPose = {
  id: "hand:holding",
  name: "Holding Object",
  deltas: {
    thumb1: thumbCmc(18),
    thumb2: flexX(34),
    thumb3: flexX(7),
    index1: flexX(55),
    index2: flexX(65),
    index3: flexX(49),
    middle1: flexX(57),
    middle2: flexX(67),
    middle3: flexX(50),
    ring1: flexX(60),
    ring2: flexX(70),
    ring3: flexX(53),
    pinky1: flexX(64),
    pinky2: flexX(75),
    pinky3: flexX(56),
  },
};

const SPREAD: HandPose = {
  id: "hand:spread",
  name: "Spread",
  deltas: {
    thumb1: thumbCmc(-18), // Strong abduction away from palm
    thumb2: flexX(-5), // Slight hyperextension
    thumb3: IDENTITY,
    index1: splayZ(-18), // Wide fan
    index2: IDENTITY,
    index3: IDENTITY,
    middle1: IDENTITY, // Reference finger — no splay
    middle2: IDENTITY,
    middle3: IDENTITY,
    ring1: splayZ(15),
    ring2: IDENTITY,
    ring3: IDENTITY,
    pinky1: splayZ(28), // Widest splay
    pinky2: IDENTITY,
    pinky3: IDENTITY,
  },
};

const POINT: HandPose = {
  id: "hand:point",
  name: "Point",
  deltas: {
    thumb1: thumbCmc(20), // Curled around middle fingers
    thumb2: flexX(40),
    thumb3: flexX(10),
    index1: IDENTITY, // Extended straight
    index2: IDENTITY,
    index3: IDENTITY,
    ...fullCurlDeltas("middle"),
    ...fullCurlDeltas("ring"),
    ...fullCurlDeltas("pinky"),
  },
};

const RELAXED: HandPose = {
  id: "hand:relaxed",
  name: "Relaxed",
  deltas: {
    thumb1: thumbCmc(8),
    thumb2: flexX(15),
    thumb3: flexX(5),
    index1: flexX(25),
    index2: flexX(30),
    index3: flexX(20),
    middle1: flexX(30),
    middle2: flexX(35),
    middle3: flexX(22),
    ring1: flexX(35),
    ring2: flexX(40),
    ring3: flexX(25),
    pinky1: flexX(40),
    pinky2: flexX(45),
    pinky3: flexX(28),
  },
};

/** All built-in hand poses, in display order. */
export const DEFAULT_HAND_POSES: HandPose[] = [
  FIST,
  SPREAD,
  RELAXED,
  POINT,
  PEACE,
  HOLDING,
];

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

const FINGERS = ["thumb", "index", "middle", "ring", "pinky"] as const;

/**
 * Build posed quaternions for one hand from a HandPose.
 * Returns a Map ready for `setBonePoses`.
 */
export function buildHandPoses(
  pose: HandPose,
  objectId: string,
  side: "L" | "R",
): Map<string, Q4> {
  const result = new Map<string, Q4>();
  for (const finger of FINGERS) {
    for (let j = 1; j <= 3; j++) {
      const key = `${finger}${j}`;
      const deltaL = pose.deltas[key] ?? IDENTITY;
      const delta = side === "R" ? mirrorQuaternion(deltaL) : deltaL;

      const boneName = `finger_${finger}${side}${j}`;
      const rest = getRestPose(objectId, boneName);
      if (!rest) continue;

      // posed = rest · delta  (post-multiply in local frame)
      result.set(boneName, qmul(rest, delta));
    }
  }
  return result;
}

/**
 * Determine which hand sides are currently selected in pose mode.
 */
export function selectedHandSides(selectedBones: Set<string>): Set<"L" | "R"> {
  const sides = new Set<"L" | "R">();
  for (const bone of selectedBones) {
    if (!(bone.startsWith("finger_") || bone.startsWith("hand"))) continue;
    if (bone.includes("L")) sides.add("L");
    if (bone.includes("R")) sides.add("R");
  }
  return sides;
}

/**
 * Apply a hand pose to one or both hands.
 * Pushes undo, then sets only the finger bone poses (body pose is untouched).
 */
export function applyHandPose(
  pose: HandPose,
  objectId: string,
  sides: Set<"L" | "R">,
): void {
  const store = useStore.getState();
  store.pushUndo();
  for (const side of sides) {
    const poses = buildHandPoses(pose, objectId, side);
    store.setBonePoses(objectId, poses);
  }
}
